import { supabase } from '../lib/supabase';
import { getDifficultyConfig } from './lumoPersonalityService';

/**
 * Adaptive Learning Service
 * Dynamically adjusts content difficulty based on user performance
 * Analyzes recent attempts to automatically level up or down
 */

// Performance thresholds for difficulty adjustment
const PERFORMANCE_THRESHOLDS = {
  levelUp: 85,      // If avg score >= 85%, increase difficulty
  levelDown: 60,    // If avg score < 60%, decrease difficulty
  analysisWindow: 10, // Analyze last N attempts
  minAttempts: 5    // Minimum attempts before adjusting
};

// Difficulty tier mapping
const DIFFICULTY_TIERS = {
  1: 'novice',
  2: 'beginner',
  3: 'intermediate',
  4: 'advanced',
  5: 'expert'
};

export const adaptiveLearningService = {
  /**
   * Analyze user's recent performance and recommend difficulty adjustment
   * @param {string} userId - User ID
   * @param {string} contentType - 'drill' or 'objection'
   * @returns {Promise<Object>} - { currentDifficulty, recommendedDifficulty, avgScore, shouldAdjust, reason }
   */
  async analyzePerformance(userId, contentType = 'drill') {
    try {
      if (!userId) throw new Error('User ID is required');

      // Fetch user's current skill level
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('skill_level, level')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      const difficultyConfig = getDifficultyConfig(profile?.skill_level || 'beginner');
      const currentDifficulty = difficultyConfig.difficultyRange[1]; // Use max of range

      // Fetch recent attempts based on content type
      let recentAttempts = [];
      
      if (contentType === 'drill') {
        const { data, error } = await supabase
          .from('user_drills')
          .select('score, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(PERFORMANCE_THRESHOLDS.analysisWindow);

        if (error) throw error;
        recentAttempts = data || [];
      } else if (contentType === 'objection') {
        const { data, error } = await supabase
          .from('user_objection_attempts')
          .select('lumo_score, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(PERFORMANCE_THRESHOLDS.analysisWindow);

        if (error) throw error;
        recentAttempts = data?.map(a => ({ score: a.lumo_score, created_at: a.created_at })) || [];
      }

      // Need minimum attempts before adjusting
      if (recentAttempts.length < PERFORMANCE_THRESHOLDS.minAttempts) {
        return {
          currentDifficulty,
          recommendedDifficulty: currentDifficulty,
          avgScore: 0,
          shouldAdjust: false,
          reason: `Need ${PERFORMANCE_THRESHOLDS.minAttempts - recentAttempts.length} more attempts before adjusting difficulty`,
          attemptsRemaining: PERFORMANCE_THRESHOLDS.minAttempts - recentAttempts.length
        };
      }

      // Calculate average score
      const avgScore = recentAttempts.reduce((sum, a) => sum + (a.score || 0), 0) / recentAttempts.length;

      // Determine recommended difficulty
      let recommendedDifficulty = currentDifficulty;
      let shouldAdjust = false;
      let reason = 'Performance is stable at current difficulty';

      if (avgScore >= PERFORMANCE_THRESHOLDS.levelUp && currentDifficulty < 5) {
        recommendedDifficulty = Math.min(currentDifficulty + 1, 5);
        shouldAdjust = true;
        reason = `🔥 Crushing it! Average score ${avgScore.toFixed(1)}% - ready for harder challenges`;
      } else if (avgScore < PERFORMANCE_THRESHOLDS.levelDown && currentDifficulty > 1) {
        recommendedDifficulty = Math.max(currentDifficulty - 1, 1);
        shouldAdjust = true;
        reason = `Let's dial it back. Average score ${avgScore.toFixed(1)}% - focus on mastering fundamentals`;
      }

      return {
        currentDifficulty,
        recommendedDifficulty,
        currentTier: DIFFICULTY_TIERS[currentDifficulty],
        recommendedTier: DIFFICULTY_TIERS[recommendedDifficulty],
        avgScore: Math.round(avgScore),
        shouldAdjust,
        reason,
        recentScores: recentAttempts.map(a => a.score),
        analysisWindow: recentAttempts.length
      };
    } catch (error) {
      console.error('Error analyzing performance:', error);
      throw error;
    }
  },

  /**
   * Apply difficulty adjustment to user profile
   * @param {string} userId - User ID
   * @param {number} newDifficulty - New difficulty level (1-5)
   * @returns {Promise<Object>} - Updated profile and XP bonus
   */
  async applyDifficultyAdjustment(userId, newDifficulty) {
    try {
      if (!userId) throw new Error('User ID is required');
      if (newDifficulty < 1 || newDifficulty > 5) {
        throw new Error('Difficulty must be between 1 and 5');
      }

      // Map difficulty to skill level
      let newSkillLevel = 'beginner';
      if (newDifficulty >= 4) newSkillLevel = 'advanced';
      else if (newDifficulty >= 3) newSkillLevel = 'intermediate';

      // Fetch current profile
      const { data: currentProfile, error: fetchError } = await supabase
        .from('user_profiles')
        .select('skill_level, xp_points')
        .eq('id', userId)
        .single();

      if (fetchError) throw fetchError;

      const wasLevelUp = newSkillLevel !== currentProfile.skill_level && 
                         ['beginner', 'intermediate', 'advanced'].indexOf(newSkillLevel) > 
                         ['beginner', 'intermediate', 'advanced'].indexOf(currentProfile.skill_level);

      // Award bonus XP for leveling up difficulty
      let xpBonus = 0;
      if (wasLevelUp) {
        xpBonus = 50; // Difficulty Mastery Bonus
      }

      // Update profile
      const { data: updatedProfile, error: updateError } = await supabase
        .from('user_profiles')
        .update({
          skill_level: newSkillLevel,
          xp_points: (currentProfile.xp_points || 0) + xpBonus,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (updateError) throw updateError;

      return {
        profile: updatedProfile,
        xpBonus,
        wasLevelUp,
        message: wasLevelUp 
          ? `🎉 Difficulty Mastery! +${xpBonus} XP for leveling up to ${newSkillLevel}` 
          : 'Difficulty adjusted based on performance'
      };
    } catch (error) {
      console.error('Error applying difficulty adjustment:', error);
      throw error;
    }
  },

  /**
   * Get dynamic difficulty for content fetching
   * Returns difficulty range based on recent performance, not just skill_level
   * @param {string} userId - User ID
   * @param {string} contentType - 'drill' or 'objection'
   * @returns {Promise<Object>} - { minDifficulty, maxDifficulty, adaptive: boolean }
   */
  async getDynamicDifficulty(userId, contentType = 'drill') {
    try {
      const analysis = await this.analyzePerformance(userId, contentType);
      
      // Use recommended difficulty if adjustment is warranted
      const targetDifficulty = analysis.shouldAdjust 
        ? analysis.recommendedDifficulty 
        : analysis.currentDifficulty;

      // Return range around target difficulty
      return {
        minDifficulty: Math.max(targetDifficulty - 1, 1),
        maxDifficulty: Math.min(targetDifficulty + 1, 5),
        targetDifficulty,
        adaptive: analysis.shouldAdjust,
        reason: analysis.reason
      };
    } catch (error) {
      console.error('Error getting dynamic difficulty:', error);
      // Fallback to beginner range
      return {
        minDifficulty: 1,
        maxDifficulty: 2,
        targetDifficulty: 1,
        adaptive: false,
        reason: 'Using default difficulty'
      };
    }
  },

  /**
   * Get performance streak for motivation
   * @param {string} userId - User ID
   * @param {string} contentType - 'drill' or 'objection'
   * @returns {Promise<Object>} - { currentStreak, bestStreak, avgScore }
   */
  async getPerformanceStreak(userId, contentType = 'drill') {
    try {
      let recentAttempts = [];
      
      if (contentType === 'drill') {
        const { data, error } = await supabase
          .from('user_drills')
          .select('score, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) throw error;
        recentAttempts = data || [];
      } else {
        const { data, error } = await supabase
          .from('user_objection_attempts')
          .select('lumo_score, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) throw error;
        recentAttempts = data?.map(a => ({ score: a.lumo_score })) || [];
      }

      if (recentAttempts.length === 0) {
        return { currentStreak: 0, bestStreak: 0, avgScore: 0 };
      }

      // Calculate streak of scores >= 75%
      let currentStreak = 0;
      let bestStreak = 0;
      let tempStreak = 0;

      for (const attempt of recentAttempts) {
        if (attempt.score >= 75) {
          tempStreak++;
          if (tempStreak === 1) currentStreak++; // Only count most recent streak
          bestStreak = Math.max(bestStreak, tempStreak);
        } else {
          if (currentStreak > 0) {
            // First miss ends current streak
            currentStreak = tempStreak;
          }
          tempStreak = 0;
        }
      }

      const avgScore = recentAttempts.reduce((sum, a) => sum + (a.score || 0), 0) / recentAttempts.length;

      return {
        currentStreak: tempStreak, // Use most recent continuous streak
        bestStreak,
        avgScore: Math.round(avgScore)
      };
    } catch (error) {
      console.error('Error getting performance streak:', error);
      return { currentStreak: 0, bestStreak: 0, avgScore: 0 };
    }
  }
};

export default adaptiveLearningService;
